import React from "react";
import styled from "styled-components";
import Skeleton from "./Skeleton";
import { isPositiveNumber } from "src/utils/helpers";
import LineChart from "./LineChart";

const CardContainer = styled.div`
    min-width: 0;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 0.65rem;
    animation: statistics-card-enter 0.22s ease both;
    transition: transform 0.16s ease;

    @keyframes statistics-card-enter {
        from {
            opacity: 0;
            transform: translateY(6px);
        }

        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ContentContainer = styled.div`
    min-height: 74px;
    margin-right: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 5.5rem;
    align-items: end;
    gap: 0.75rem;
    padding: 0.65rem 1rem 0.9rem;
    overflow: hidden;
    box-sizing: border-box;

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    height: 100%;
    justify-content: flex-end;
    align-items: flex-start;
    flex-direction: column;
    min-width: 0;
`;

const Card = styled.div`
    width: 100%;
    min-height: ${p => p.cardHeight || "116px"};
    background: #FFFFFF 0 0 no-repeat padding-box;
    box-shadow: ${p => p.cardShadow || "0 18px 36px rgba(29, 42, 89, 0.08)"};
    border: 1px solid #D8DEEC;
    border-radius: 12px;
    overflow: hidden;
    transition: transform 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease;

    &:hover {
        border-color: #C9D2E7;
        box-shadow: ${p => p.cardShadow || "0 14px 28px rgba(29, 42, 89, 0.09)"};
        transform: translateY(-1px);
    }
`;

const CardHeader = styled.div`
    min-height: 2.25rem;
    margin: 0;
    padding: 0.75rem 1rem;
    width: auto;
    text-align: left;
    font-size: .75rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    line-height: 1.35;
    text-transform: uppercase;
    color: #171A22;
    opacity: 1;
    overflow-wrap: anywhere;
`;

const Hr = styled.div`
    height: 1px;
    background: #D8DEEC;
`;

const TextLabel = styled.div`
    text-align: left;
    font: normal normal 500 0.8125rem/1.45 Manrope;
    letter-spacing: 0;
    color: #6A7387;
    opacity: 1;
    margin: 0;
    min-height: 2.375rem;
    overflow-wrap: anywhere;
`;

const Number = styled.div`
    text-align: left;
    font: normal normal 800 1.8rem/1.1 Manrope;
    letter-spacing: 0;
    margin: 0;
    color: #171A22;
    opacity: 1;
    max-width: 100%;
    overflow-wrap: anywhere;

    @media (max-width: 520px) {
        font-size: 1.55rem;
    }
`;

const NumberPercentage = styled.div`
    text-align: left;
    font-size: .8rem;
    letter-spacing: 0;
    margin: 0.35rem 0 0;
    color: #02BC77;
    opacity: 1;
`;

const NegativeNumberPercentage = styled.div`
    text-align: left;
    font-size: .8rem;
    letter-spacing: 0;
    margin: 0.35rem 0 0;
    color: #FF2366;
    opacity: 1;
`;

const SkeletonContainer = styled.div`
    margin: .5rem 1rem;
`;

const LineChartContainer = styled.div`
    width: 5.5rem;
    height: 3.5rem;
    justify-self: end;
    align-self: end;
    min-width: 0;

    @media (max-width: 520px) {
        width: 100%;
        height: 3rem;
        justify-self: stretch;
    }
`;

const StatisticsCard = (props, children) => {
    const { header, footer, loadingBaseNumbers, graphData, rate, total, loadingLineGraph, cardHeight, printFormat } = props;
    return (
        <>
            <CardContainer>
                <Card cardHeight={cardHeight} cardShadow={printFormat ? "unset" : undefined}>
                    <CardHeader>
                        {loadingBaseNumbers ?
                            <Skeleton variant="Text" width="100px" height="20px" animation="wave" />
                            : header
                        }
                    </CardHeader>
                    <Hr />
                    {loadingBaseNumbers ?
                        <SkeletonContainer>
                            <Skeleton variant="Text" width="50px" height="33px" animation="wave" />
                        </SkeletonContainer>
                        :
                        <ContentContainer>
                            <ContentWrapper>
                                <Number>{total}</Number>
                                {(!rate && rate !== 0) ? "" : (isPositiveNumber(rate) ? <NumberPercentage>+{rate}%</NumberPercentage> : <NegativeNumberPercentage>{rate}%</NegativeNumberPercentage>) }
                            </ContentWrapper>
                            <LineChartContainer>
                                {loadingLineGraph ?
                                    <SkeletonContainer>
                                        <Skeleton variant="Text" width="60px" height="40px" animation="wave" />
                                    </SkeletonContainer>
                                    :
                                    <LineChart
                                        data={graphData}
                                        isPositive={isPositiveNumber(rate)}
                                    />}
                            </LineChartContainer>
                        </ContentContainer>
                    }
                </Card>
                <TextLabel size=".5rem">
                    {footer}
                </TextLabel>
            </CardContainer>
        </>
    );
};
export default StatisticsCard;
